import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Download, FileText, Printer, Share, Stethoscope } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import SignUpPrompt from '@/components/auth/SignUpPrompt';
import jsPDF from 'jspdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } from 'docx';
interface EnhancedExportButtonProps {
  answers: any;
  aiScore: number;
  riskLevel: string;
  confidence: number;
  enhancedData?: any;
  potentialDiagnoses?: string[];
  recommendations?: string[];
  className?: string;
  compact?: boolean; // New prop for header version
}
const EnhancedExportButton = ({
  answers,
  aiScore,
  riskLevel,
  confidence,
  enhancedData,
  potentialDiagnoses = [],
  recommendations = [],
  className = "",
  compact = false
}: EnhancedExportButtonProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const isMobile = useIsMobile();
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const handleExportClick = () => {
    if (!user) {
      setShowAuthPrompt(true);
      return;
    }
    setIsDialogOpen(true);
  };
  const generateReport = (format: 'pdf' | 'docx' | 'odt' | 'rtf' | 'txt' | 'xlsx' | 'csv' | 'tsv' | 'text' | 'word' | 'notes') => {
    const currentDate = new Date().toLocaleDateString('en-GB');
    const currentTime = new Date().toLocaleTimeString('en-GB');

    // Extract symptoms from answers
    const symptoms = answers?.symptom_constellation?.selections || [];
    const systemicSymptoms = (answers?.systemic_symptoms || []).filter((s: string) => s !== "None of the above");
    const duration = answers?.symptom_duration || "Not specified";
    const pattern = answers?.symptom_pattern || "Not specified";
    const dailyImpact = answers?.daily_impact || "Not specified";
    const reportContent = {
      header: `
═══════════════════════════════════════════════════════════════
                        AmIOK HEALTH ASSESSMENT REPORT
═══════════════════════════════════════════════════════════════

Generated: ${currentDate} at ${currentTime}
Report ID: AMIOK-${Date.now()}
Patient Reference: [To be completed by healthcare provider]

═══════════════════════════════════════════════════════════════
                              CLINICAL SUMMARY
═══════════════════════════════════════════════════════════════`,
      assessment: `
ASSESSMENT SCORES:
• AmIOK Risk Score: ${aiScore}/100
• Risk Classification: ${riskLevel}
• AI Confidence Level: ${confidence}%
• Assessment Date: ${currentDate}

PRESENTING SYMPTOMS:
Primary Concerns:
${symptoms.map((s: string, i: number) => `  ${i + 1}. ${s}`).join('\n')}

Additional Systemic Symptoms:
${systemicSymptoms.length > 0 ? systemicSymptoms.map((s: string, i: number) => `  ${i + 1}. ${s}`).join('\n') : '  None reported'}

SYMPTOM CHARACTERISTICS:
• Duration: ${duration}
• Pattern: ${pattern}
• Daily Life Impact: ${dailyImpact}/10

POTENTIAL DIAGNOSES:
${potentialDiagnoses.length > 0 ? potentialDiagnoses.map((d: string, i: number) => `  ${i + 1}. ${d}`).join('\n') : '  Assessment pending healthcare provider evaluation'}`,
      recommendations: `
═══════════════════════════════════════════════════════════════
                           CLINICAL RECOMMENDATIONS
═══════════════════════════════════════════════════════════════

IMMEDIATE ACTIONS:
${aiScore >= 80 ? `• URGENT: Seek immediate medical attention - Contact 999
• Do not delay seeking emergency care
• Present this report to emergency services` : aiScore >= 60 ? `• PROMPT: Contact NHS 111 or arrange urgent GP appointment
• Monitor symptoms closely
• Seek medical evaluation within 24-48 hours` : `• ROUTINE: Schedule appointment with GP if symptoms persist
• Continue monitoring symptoms
• Follow general health maintenance advice`}

ONGOING CARE:
${recommendations.length > 0 ? recommendations.map((r: string, i: number) => `  ${i + 1}. ${r}`).join('\n') : `  • Follow guidance provided in assessment
  • Monitor symptom progression
  • Maintain regular health check-ups`}

MONITORING PLAN:
• Track symptom changes daily
• Note any new or worsening symptoms
• Monitor response to any treatments
• Keep symptom diary for healthcare provider

WHEN TO SEEK URGENT CARE:
• Severe worsening of symptoms
• New concerning symptoms develop
• Emergency warning signs appear
• Significant deterioration in daily functioning`,
      contacts: `
═══════════════════════════════════════════════════════════════
                         IMPORTANT CONTACTS
═══════════════════════════════════════════════════════════════

EMERGENCY SERVICES:
• Emergency (Life-threatening): 999
• Non-emergency urgent care: 111
• Crisis support (24/7): 116 123

PRIMARY CARE:
• GP Surgery: [Contact your registered practice]
• NHS Walk-in Centre: [Visit NHS website for locations]
• Out-of-hours service: Contact NHS 111

SPECIALIST SERVICES:
• Contact GP for specialist referrals if indicated
• Expected referral timeframe: 2-4 weeks via GP`,
      disclaimer: `
═══════════════════════════════════════════════════════════════
                           IMPORTANT DISCLAIMER
═══════════════════════════════════════════════════════════════

This assessment report is generated by AmIOK AI Health Assessment 
technology and is intended for informational purposes only.

⚠️  MEDICAL DISCLAIMER:
• This report does NOT constitute medical diagnosis
• It should NOT replace professional medical advice
• Always consult qualified healthcare professionals
• For emergencies, contact 999 immediately

The AI assessment is based on reported symptoms and clinical 
algorithms. Healthcare providers should conduct their own 
independent clinical evaluation.

Report generated by: AmIOK Clinical Decision Support System
Technology Partner: Evidence-based AI Medical Analysis
Data Protection: GDPR Compliant | NHS Guidelines Adherent

For technical support: support@amiok.health
Medical queries: Consult your healthcare provider

═══════════════════════════════════════════════════════════════
                              END OF REPORT
═══════════════════════════════════════════════════════════════`
    };
    const fullReport = `${reportContent.header}${reportContent.assessment}${reportContent.recommendations}${reportContent.contacts}${reportContent.disclaimer}`;
    // Generate structured data for different formats
    const structuredData = {
      assessmentDate: currentDate,
      reportId: `AMIOK-${Date.now()}`,
      score: aiScore,
      riskLevel: riskLevel,
      confidence: confidence,
      symptoms: symptoms,
      systemicSymptoms: systemicSymptoms,
      duration: duration,
      pattern: pattern,
      dailyImpact: dailyImpact,
      diagnoses: potentialDiagnoses,
      recommendations: recommendations
    };

    switch (format) {
      case 'pdf':
        // Generate actual PDF using jsPDF
        const pdf = new jsPDF();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const margin = 20;
        let yPosition = margin;
        
        // Helper function to add text with line wrapping
        const addWrappedText = (text: string, fontSize: number = 12, isBold: boolean = false) => {
          pdf.setFontSize(fontSize);
          if (isBold) pdf.setFont(undefined, 'bold');
          else pdf.setFont(undefined, 'normal');
          
          const lines = pdf.splitTextToSize(text, pageWidth - 2 * margin);
          lines.forEach((line: string) => {
            if (yPosition > pdf.internal.pageSize.getHeight() - margin) {
              pdf.addPage();
              yPosition = margin;
            }
            pdf.text(line, margin, yPosition);
            yPosition += fontSize * 0.5;
          });
          yPosition += 5; // Add some space after each section
        };

        // Add header
        addWrappedText('AmIOK Health Assessment Report', 18, true);
        addWrappedText(`Generated: ${currentDate} at ${currentTime}`, 10);
        addWrappedText(`Report ID: AMIOK-${Date.now()}`, 10);
        yPosition += 10;

        // Add assessment scores
        addWrappedText('ASSESSMENT SUMMARY', 14, true);
        addWrappedText(`AmIOK Risk Score: ${aiScore}/100`, 12, true);
        addWrappedText(`Risk Classification: ${riskLevel}`, 12, true);
        addWrappedText(`AI Confidence Level: ${confidence}%`, 12, true);
        yPosition += 10;

        // Add symptoms
        addWrappedText('PRESENTING SYMPTOMS', 14, true);
        addWrappedText('Primary Concerns:', 12, true);
        symptoms.forEach((symptom: string, index: number) => {
          addWrappedText(`${index + 1}. ${symptom}`, 11);
        });
        
        if (systemicSymptoms.length > 0) {
          addWrappedText('Additional Systemic Symptoms:', 12, true);
          systemicSymptoms.forEach((symptom: string, index: number) => {
            addWrappedText(`${index + 1}. ${symptom}`, 11);
          });
        }

        addWrappedText(`Duration: ${duration}`, 11);
        addWrappedText(`Pattern: ${pattern}`, 11);
        addWrappedText(`Daily Life Impact: ${dailyImpact}/10`, 11);
        yPosition += 10;

        // Add recommendations
        addWrappedText('CLINICAL RECOMMENDATIONS', 14, true);
        if (aiScore >= 80) {
          addWrappedText('URGENT: Seek immediate medical attention - Contact 999', 12, true);
        } else if (aiScore >= 60) {
          addWrappedText('PROMPT: Contact NHS 111 or arrange urgent GP appointment', 12, true);
        } else {
          addWrappedText('ROUTINE: Schedule appointment with GP if symptoms persist', 12, true);
        }

        yPosition += 10;
        addWrappedText('IMPORTANT DISCLAIMER', 14, true);
        addWrappedText('This assessment report is for informational purposes only and does NOT constitute medical diagnosis. Always consult qualified healthcare professionals for medical concerns. For emergencies, contact 999 immediately.', 10);

        // Save the PDF
        pdf.save(`AmIOK-Report-${currentDate.replace(/\//g, '-')}.pdf`);
        break;

      case 'word':
      case 'docx':
        // Generate actual Word document using docx library
        const doc = new Document({
          sections: [{
            properties: {},
            children: [
              new Paragraph({
                text: "AmIOK Health Assessment Report",
                heading: HeadingLevel.TITLE,
                alignment: AlignmentType.CENTER,
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Generated: ${currentDate} at ${currentTime}`,
                    size: 20,
                  }),
                ],
                spacing: { after: 200 },
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Report ID: AMIOK-${Date.now()}`,
                    size: 20,
                  }),
                ],
                spacing: { after: 400 },
              }),
              
              new Paragraph({
                text: "Assessment Summary",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `AmIOK Risk Score: ${aiScore}/100`,
                    bold: true,
                    size: 24,
                  }),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Risk Classification: ${riskLevel}`,
                    bold: true,
                    size: 24,
                  }),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `AI Confidence Level: ${confidence}%`,
                    bold: true,
                    size: 24,
                  }),
                ],
                spacing: { after: 300 },
              }),
              
              new Paragraph({
                text: "Presenting Symptoms",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Primary Concerns:",
                    bold: true,
                  }),
                ],
              }),
              ...symptoms.map((symptom: string, index: number) => 
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index + 1}. ${symptom}`,
                    }),
                  ],
                })
              ),
              
              ...(systemicSymptoms.length > 0 ? [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Additional Systemic Symptoms:",
                      bold: true,
                    }),
                  ],
                }),
                ...systemicSymptoms.map((symptom: string, index: number) => 
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `${index + 1}. ${symptom}`,
                      }),
                    ],
                  })
                )
              ] : []),
              
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Duration: ${duration}`,
                  }),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Pattern: ${pattern}`,
                  }),
                ],
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Daily Life Impact: ${dailyImpact}/10`,
                  }),
                ],
                spacing: { after: 300 },
              }),
              
              new Paragraph({
                text: "Clinical Recommendations",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: aiScore >= 80 ? 
                      "URGENT: Seek immediate medical attention - Contact 999" :
                      aiScore >= 60 ?
                      "PROMPT: Contact NHS 111 or arrange urgent GP appointment" :
                      "ROUTINE: Schedule appointment with GP if symptoms persist",
                    bold: true,
                    color: aiScore >= 60 ? "CC0000" : "0066CC",
                  }),
                ],
                spacing: { after: 300 },
              }),
              
              new Paragraph({
                text: "Important Disclaimer",
                heading: HeadingLevel.HEADING_1,
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "This assessment report is for informational purposes only and does NOT constitute medical diagnosis. Always consult qualified healthcare professionals for medical concerns. For emergencies, contact 999 immediately.",
                    italics: true,
                    size: 20,
                  }),
                ],
              }),
            ],
          }],
        });

        // Generate and download the Word document
        Packer.toBlob(doc).then(blob => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `AmIOK-Report-${currentDate.replace(/\//g, '-')}.docx`;
          link.click();
          URL.revokeObjectURL(url);
        });
        break;
      case 'text':
      case 'txt':
        downloadFile(fullReport, `AmIOK-Report-${currentDate.replace(/\//g, '-')}.txt`, 'text/plain');
        break;
      case 'rtf':
        // Generate RTF format
        const rtfContent = `{\\rtf1\\ansi\\deff0 {\\fonttbl {\\f0 Times New Roman;}}
\\f0\\fs24 AmIOK Health Assessment Report\\par
\\line
Generated: ${currentDate} at ${currentTime}\\par
Report ID: AMIOK-${Date.now()}\\par
\\line
Assessment Score: ${aiScore}/100\\par
Risk Level: ${riskLevel}\\par
Confidence: ${confidence}%\\par
\\line
Symptoms: ${symptoms.join(', ')}\\par
Duration: ${duration}\\par
Pattern: ${pattern}\\par
Daily Impact: ${dailyImpact}/10\\par
\\line
${fullReport.replace(/\n/g, '\\par ')}}`;
        downloadFile(rtfContent, `AmIOK-Report-${currentDate.replace(/\//g, '-')}.rtf`, 'application/rtf');
        break;
      case 'odt':
        // Generate simple ODT format (basic XML structure)
        const odtContent = `<?xml version="1.0" encoding="UTF-8"?>
<office:document-content xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0">
<office:body>
<office:text>
<text:h text:style-name="Heading">AmIOK Health Assessment Report</text:h>
<text:p>Generated: ${currentDate} at ${currentTime}</text:p>
<text:p>Report ID: AMIOK-${Date.now()}</text:p>
<text:p>Assessment Score: ${aiScore}/100</text:p>
<text:p>Risk Level: ${riskLevel}</text:p>
<text:p>Confidence: ${confidence}%</text:p>
<text:p>Symptoms: ${symptoms.join(', ')}</text:p>
<text:p>Duration: ${duration}</text:p>
<text:p>Pattern: ${pattern}</text:p>
<text:p>Daily Impact: ${dailyImpact}/10</text:p>
${fullReport.split('\n').map(line => `<text:p>${line}</text:p>`).join('')}
</office:text>
</office:body>
</office:document-content>`;
        downloadFile(odtContent, `AmIOK-Report-${currentDate.replace(/\//g, '-')}.odt`, 'application/vnd.oasis.opendocument.text');
        break;
      case 'xlsx':
        // Generate CSV-like content for Excel compatibility
        const csvForExcel = `AmIOK Health Assessment Report
Generated,${currentDate} ${currentTime}
Report ID,AMIOK-${Date.now()}

Assessment Data
Score,${aiScore}/100
Risk Level,${riskLevel}
Confidence,${confidence}%
Duration,${duration}
Pattern,${pattern}
Daily Impact,${dailyImpact}/10

Symptoms
${symptoms.map((s, i) => `Symptom ${i + 1},${s}`).join('\n')}

Additional Symptoms
${systemicSymptoms.map((s, i) => `Additional ${i + 1},${s}`).join('\n')}

Potential Diagnoses
${potentialDiagnoses.map((d, i) => `Diagnosis ${i + 1},${d}`).join('\n')}`;
        downloadFile(csvForExcel, `AmIOK-Report-${currentDate.replace(/\//g, '-')}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        break;
      case 'csv':
        const csvContent = `Assessment Date,Score,Risk Level,Confidence,Duration,Pattern,Daily Impact,Primary Symptoms,Additional Symptoms
${currentDate},${aiScore}/100,${riskLevel},${confidence}%,${duration},${pattern},${dailyImpact}/10,"${symptoms.join('; ')}","${systemicSymptoms.join('; ')}"`;
        downloadFile(csvContent, `AmIOK-Report-${currentDate.replace(/\//g, '-')}.csv`, 'text/csv');
        break;
      case 'tsv':
        const tsvContent = `Assessment Date\tScore\tRisk Level\tConfidence\tDuration\tPattern\tDaily Impact\tPrimary Symptoms\tAdditional Symptoms
${currentDate}\t${aiScore}/100\t${riskLevel}\t${confidence}%\t${duration}\t${pattern}\t${dailyImpact}/10\t${symptoms.join('; ')}\t${systemicSymptoms.join('; ')}`;
        downloadFile(tsvContent, `AmIOK-Report-${currentDate.replace(/\//g, '-')}.tsv`, 'text/tab-separated-values');
        break;
      case 'notes':
        // Simplified format for notes apps
        const notesFormat = `AmIOK Health Assessment - ${currentDate}

SCORE: ${aiScore}/100 (${riskLevel})
CONFIDENCE: ${confidence}%

SYMPTOMS:
${symptoms.map((s: string) => `• ${s}`).join('\n')}

ADDITIONAL: ${systemicSymptoms.join(', ') || 'None'}

DURATION: ${duration}
PATTERN: ${pattern}  
IMPACT: ${dailyImpact}/10

NEXT STEPS:
${aiScore >= 80 ? '• URGENT: Contact 999 immediately' : aiScore >= 60 ? '• PROMPT: Contact NHS 111 or GP urgently' : '• ROUTINE: Schedule GP appointment if needed'}

IMPORTANT: This is AI-generated guidance. Always consult healthcare professionals for medical advice.

Emergency: 999 | Non-emergency: 111 | Crisis: 116 123`;
        if (navigator.share) {
          navigator.share({
            title: 'AmIOK Health Assessment',
            text: notesFormat
          });
        } else {
          navigator.clipboard.writeText(notesFormat);
          alert('Report copied to clipboard!');
        }
        break;
    }
    setIsDialogOpen(false);
  };
  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], {
      type: mimeType
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Show authentication prompt dialog
  if (showAuthPrompt) {
    return <Dialog open={showAuthPrompt} onOpenChange={setShowAuthPrompt}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Account Required</DialogTitle>
            <DialogDescription>
              Please create an account or log in to export your health assessment report.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4">
            <SignUpPrompt onDismiss={() => setShowAuthPrompt(false)} />
          </div>
        </DialogContent>
      </Dialog>;
  }

  // Compact version for header
  if (compact) {
    return <>
        <Button onClick={handleExportClick} variant="outline" size="sm" className={`text-blue-600 hover:text-blue-700 text-sm px-3 py-2 flex items-center gap-2 ${className}`}>
          <Download className="h-4 w-4 flex-shrink-0" />
          <span className="hidden sm:inline">Download Report</span>
        </Button>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Export Assessment Report</DialogTitle>
              <DialogDescription>
                Choose your preferred format for sharing with healthcare providers
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-2 mt-4 max-h-96 overflow-y-auto">
              <Button onClick={() => generateReport('pdf')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-red-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">PDF (.pdf)</div>
                  <div className="text-xs text-muted-foreground">Fixed layout, universally readable, ideal for final reports</div>
                </div>
              </Button>
              
              <Button onClick={() => generateReport('docx')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-blue-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">Word Document (.docx)</div>
                  <div className="text-xs text-muted-foreground">Editable in Microsoft Word and other editors</div>
                </div>
              </Button>
              
              <Button onClick={() => generateReport('odt')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-green-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">OpenDocument Text (.odt)</div>
                  <div className="text-xs text-muted-foreground">Open standard alternative to Word</div>
                </div>
              </Button>
              
              <Button onClick={() => generateReport('rtf')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-orange-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">Rich Text Format (.rtf)</div>
                  <div className="text-xs text-muted-foreground">Basic formatting, readable by most word processors</div>
                </div>
              </Button>
              
              <Button onClick={() => generateReport('txt')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-gray-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">Plain Text (.txt)</div>
                  <div className="text-xs text-muted-foreground">Unformatted, smallest file size</div>
                </div>
              </Button>
              
              <Button onClick={() => generateReport('xlsx')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-emerald-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">Excel Workbook (.xlsx)</div>
                  <div className="text-xs text-muted-foreground">For editable tables, formulas, charts</div>
                </div>
              </Button>
              
              <Button onClick={() => generateReport('csv')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-teal-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">CSV (.csv)</div>
                  <div className="text-xs text-muted-foreground">Raw tabular data, easily imported into analytics tools</div>
                </div>
              </Button>
              
              <Button onClick={() => generateReport('tsv')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-cyan-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">TSV (.tsv)</div>
                  <div className="text-xs text-muted-foreground">Like CSV but uses tabs instead of commas</div>
                </div>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>;
  }

  // Full card version for main content area
  return <Card className={`bg-gradient-to-r from-blue-50 to-green-50 border-blue-200 ${className}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Download className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Your Healthcare Report</h3>
              <p className="text-sm text-gray-600">Download your comprehensive health assessment</p>
            </div>
          </div>
          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
            Professional Report
          </Badge>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleExportClick} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
              <Download className="h-4 w-4 mr-2" />
              Download Report
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Export Assessment Report</DialogTitle>
              <DialogDescription>
                Choose your preferred format for sharing with healthcare providers
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-2 mt-4 max-h-96 overflow-y-auto">
              <Button onClick={() => generateReport('pdf')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-red-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">PDF (.pdf)</div>
                  <div className="text-xs text-muted-foreground">Fixed layout, universally readable, ideal for final reports</div>
                </div>
              </Button>
              
              <Button onClick={() => generateReport('docx')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-blue-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">Word Document (.docx)</div>
                  <div className="text-xs text-muted-foreground">Editable in Microsoft Word and other editors</div>
                </div>
              </Button>
              
              <Button onClick={() => generateReport('odt')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-green-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">OpenDocument Text (.odt)</div>
                  <div className="text-xs text-muted-foreground">Open standard alternative to Word</div>
                </div>
              </Button>
              
              <Button onClick={() => generateReport('rtf')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-orange-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">Rich Text Format (.rtf)</div>
                  <div className="text-xs text-muted-foreground">Basic formatting, readable by most word processors</div>
                </div>
              </Button>
              
              <Button onClick={() => generateReport('txt')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-gray-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">Plain Text (.txt)</div>
                  <div className="text-xs text-muted-foreground">Unformatted, smallest file size</div>
                </div>
              </Button>
              
              <Button onClick={() => generateReport('xlsx')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-emerald-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">Excel Workbook (.xlsx)</div>
                  <div className="text-xs text-muted-foreground">For editable tables, formulas, charts</div>
                </div>
              </Button>
              
              <Button onClick={() => generateReport('csv')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-teal-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">CSV (.csv)</div>
                  <div className="text-xs text-muted-foreground">Raw tabular data, easily imported into analytics tools</div>
                </div>
              </Button>
              
              <Button onClick={() => generateReport('tsv')} variant="outline" className="flex items-center justify-start gap-3 h-auto p-3">
                <FileText className="h-4 w-4 text-cyan-600" />
                <div className="text-left">
                  <div className="font-medium text-sm">TSV (.tsv)</div>
                  <div className="text-xs text-muted-foreground">Like CSV but uses tabs instead of commas</div>
                </div>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Card>;
};
export default EnhancedExportButton;